import { FailoverFullRow, GatewayFullRow, InferencerType, LoadBalancerFullRow, ModelRow, RaceFullRow, RouterFullRow, NamirasoftInferenceServer } from "namirasoft-inference";
import { InferenceRoutes } from "../InferenceRoutes";

export interface InferencerTreeChild
{
    label?: string;
    node: InferencerTreeNode;
}

export interface InferencerTreeNode
{
    id: string;
    type: string;
    entity_id: string;
    name: string;
    subtitle?: string;
    meta?: string[];
    href: string;
    children: InferencerTreeChild[];
}

const MAX_DEPTH = 6;

export class InferencerTree
{
    private counter = 0;
    private inference: NamirasoftInferenceServer;

    constructor(inference: NamirasoftInferenceServer)
    {
        this.inference = inference;
    }

    static async load(inference: NamirasoftInferenceServer, type: InferencerType | string, id: string): Promise<InferencerTreeNode>
    {
        let loader = new InferencerTree(inference);
        return loader.load(String(type).toUpperCase(), id);
    }

    private nextId(): string { this.counter += 1; return `n${this.counter}`; }

    async load(type: string, id: string): Promise<InferencerTreeNode>
    {
        this.counter = 0;
        return this.fetchNode(type, id, 0, new Set());
    }

    private async fetchNode(type: string, id: string, depth: number, visited: Set<string>): Promise<InferencerTreeNode>
    {
        let key = `${type}:${id}`;
        let node: InferencerTreeNode = { id: this.nextId(), type, entity_id: id, name: id, href: "", children: [] };
        if (visited.has(key) || depth > MAX_DEPTH)
        {
            node.name = `${type} (recursive)`;
            return node;
        }
        let nextVisited = new Set(visited);
        nextVisited.add(key);

        switch (type)
        {
            case "MODEL":
                {
                    let row: ModelRow = await this.inference.model.Get(id);
                    node.name = row.name;
                    node.subtitle = `${row.model_company} · ${row.model_name}`;
                    node.href = InferenceRoutes.ModelView(row.id);
                    break;
                }
            case "LOAD_BALANCER":
                {
                    let row: LoadBalancerFullRow = await this.inference.load_balancer.Get(id);
                    let targets = (row.targets ?? []).slice().sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0));
                    node.name = row.name;
                    node.subtitle = `Algorithm: ${row.algorithm}`;
                    node.meta = [`${targets.length} target${targets.length === 1 ? "" : "s"}`];
                    node.href = InferenceRoutes.LoadBalancerView(row.id);
                    for (let t of targets)
                    {
                        let child = await this.fetchNode(t.inferencer_type, t.inferencer_id, depth + 1, nextVisited);
                        node.children.push({ label: `Weight ${t.weight}`, node: child });
                    }
                    break;
                }
            case "FAILOVER":
                {
                    let row: FailoverFullRow = await this.inference.failover.Get(id);
                    let targets = (row.targets ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                    node.name = row.name;
                    node.meta = [`${targets.length} target${targets.length === 1 ? "" : "s"}`];
                    node.href = InferenceRoutes.FailoverView(row.id);
                    for (let i = 0; i < targets.length; i++)
                    {
                        let t = targets[i];
                        let child = await this.fetchNode(t.inferencer_type, t.inferencer_id, depth + 1, nextVisited);
                        node.children.push({ label: `#${t.order ?? i + 1} · ${t.timeout}ms`, node: child });
                    }
                    break;
                }
            case "RACE":
                {
                    let row: RaceFullRow = await this.inference.race.Get(id);
                    let targets = row.targets ?? [];
                    node.name = row.name;
                    node.meta = [`${targets.length} target${targets.length === 1 ? "" : "s"}`];
                    node.href = InferenceRoutes.RaceView(row.id);
                    for (let i = 0; i < targets.length; i++)
                    {
                        let t = targets[i];
                        let child = await this.fetchNode(t.inferencer_type, t.inferencer_id, depth + 1, nextVisited);
                        node.children.push({ label: `Contender ${i + 1}`, node: child });
                    }
                    break;
                }
            case "ROUTER":
                {
                    let row: RouterFullRow = await this.inference.router.Get(id);
                    let routes = (row.routes ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                    node.name = row.name;
                    node.meta = [`${routes.length} route${routes.length === 1 ? "" : "s"}`];
                    node.href = InferenceRoutes.RouterView(row.id);
                    for (let r of routes)
                    {
                        let child = await this.fetchNode(r.inferencer_type, r.inferencer_id, depth + 1, nextVisited);
                        node.children.push({ label: `Route ${r.order}${r.ccg_not ? " · NOT" : ""} · ${r.ccg_type}`, node: child });
                    }
                    if (row.default_inferencer_id)
                    {
                        let child = await this.fetchNode(row.default_inferencer_type, row.default_inferencer_id, depth + 1, nextVisited);
                        node.children.push({ label: "Default", node: child });
                    }
                    break;
                }
            case "GATEWAY":
                {
                    let row: GatewayFullRow = await this.inference.gateway.Get(id);
                    let guardRailsCount = (row.guard_rails ?? []).length;
                    let knowledgebasesCount = (row.knowledgebases ?? []).length;
                    let meta: string[] = [];
                    if (guardRailsCount > 0) meta.push(`${guardRailsCount} guard rail${guardRailsCount === 1 ? "" : "s"}`);
                    if (knowledgebasesCount > 0) meta.push(`${knowledgebasesCount} knowledgebase${knowledgebasesCount === 1 ? "" : "s"}`);
                    if (row.validator_id) meta.push("validator");
                    node.name = row.name;
                    node.meta = meta;
                    node.href = InferenceRoutes.GatewayView(row.id);
                    if (row.inferencer_id)
                    {
                        let child = await this.fetchNode(row.inferencer_type, row.inferencer_id, depth + 1, nextVisited);
                        node.children.push({ label: "Inferencer", node: child });
                    }
                    break;
                }
            default:
                throw new Error(`Unsupported inferencer type: ${type}`);
        }
        return node;
    }
}